import {
  P2GAdaptiveOverlay,
  P2GAlert,
  P2GBox,
  P2GContainer,
  P2GGrid,
  P2GLink,
  P2GText,
  useP2GMediaQueryOnly
} from '@parcel2go/core-ui';
import { Suspense, lazy, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { Basket } from '~/components/basket-summary/basket/basket';
import CTAButton from '~/components/core/button/cta-button';
import Stepper from '~/components/core/stepper/stepper';
import Loading from '~/components/core/progress/loading';
import useBasket from '~/hooks/use-basket';
import { TranslationNamespaces } from '~/lib/constants';
import { BASKETITEMID } from '~/lib/getCurrentItemId';
import { BasketMeta, BasketQuote, GenericErrorResponse } from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';
import { Workflow } from '~/lib/types/workflow-types';
import { GenerateWorkflow } from '~/lib/workflow/generate-workflow';
import Total from '~/components/basket-summary/basket/total';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { validateItem } from '~/lib/item-validation';
import { BasketProcessing } from './processing';
import DiscountCode from '~/components/basket-summary/basket/discount-code';
import { ToStepperTitle } from '~/components/core/stepper/to-stepper-title';
import { endReplay, publishTags } from '~/lib/metrics';
import { GetUnderpayments } from '~/lib/basket-breakdown';
import { useAuth } from 'react-oidc-context';
import { basketItemToRemoveItemEvent, basketToBeginCheckoutEvent, basketToViewCheckoutEvent } from '~/lib/analytics';
import AddAnother from '~/components/basket-summary/basket/add-another';
import { fullProtectionPrice } from '~/lib/quotes';
import BasketProtectionDialog from '~/components/basket-summary/basket/basket-protection-dialog';
import Outstanding from '~/components/address-details/address/outstanding';
import { PublicSettings } from '~/lib/ui-serverless-settings';
import EmptyBasketMessage from '~/components/basket-summary/basket/empty-basket';

const CmsContent = lazy(() => import('~/components/core/content/cms-content'));

export function Component() {
  const { isLoading } = useAuth();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const {
    id,
    items,
    change,
    createOrder,
    loading,
    remove,
    duplicate,
    validateBasket,
    addDiscountCode,
    discountCode,
    validating,
    upgradeProtection,
    validation
  } = useBasket();
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [showProtection, setShowProtection] = useState(false);
  const isXs = useP2GMediaQueryOnly('xs');
  const [creating, setCreating] = useState<boolean>(false);
  const [creationErrors, setCreationErrors] = useState<GenericErrorResponse>();
  const { track, end } = useAnalytics();
  const [payment, setPayment] = useState<{ orderId: string; hash: string } | null>(null);
  const { settings } = useTenant();
  const viewCartEventFired = useRef(false);

  useEffect(() => setCreating(false), []);

  const ready = useMemo(() => !loading && !isLoading, [loading, isLoading]);

  useEffect(() => {
    if (ready) {
      track({ type: 'basket_view', payload: items, persist: false });
    }
    document.title = 'Order Confirm';
  }, [ready]);

  const basketItems = useMemo(() => {
    if (!ready) {
      return [];
    }
    return Object.entries(items)
      .map(([name, item]) => ({
        name: name,
        item: item
      }))
      .filter((item) => (item.item.sections[BasketSections.META]?.payload as BasketMeta)?.completed === true);
  }, [items, ready]);

  const stagedBasketItems = useMemo(() => {
    if (!ready) {
      return [];
    }
    return Object.entries(items)
      .map(([name, item]) => ({
        name: name,
        item: item
      }))
      .filter(
        (item) =>
          (item.item.sections[BasketSections.META]?.payload as BasketMeta)?.completed === false &&
          (item.item.sections[BasketSections.META]?.payload as BasketMeta)?.staging === true
      );
  }, [items, ready]);

  useEffect(() => {
    if (ready && basketItems.length > 0) {
      validateBasket();
    }
  }, [basketItems, validateBasket, ready]);

  useEffect(() => {
    if (!ready || validating || basketItems.length === 0 || !validation?.breakdown?.total) return;

    if (!viewCartEventFired.current) {
      track({
        type: 'view_cart',
        payload: basketToViewCheckoutEvent(basketItems, validation.breakdown)
      });
      viewCartEventFired.current = true;
    }

    track({
      type: 'viewed_cart',
      payload: {
        id
      }
    });
  }, [basketItems, ready, track, validating, validation]);

  const hasQuoteErrors = useMemo(
    () =>
      Object.entries(basketItems).some(
        ([, item]) =>
          ((item.item.sections[BasketSections.QUOTE]?.payload as BasketQuote)?.errors?.length ?? 0) > 0 ||
          validateItem(item.name, item.item).length > 0
      ),
    [basketItems]
  );

  const onEdit = useCallback(
    (idToEdit: string, action: string) => {
      const wf = GenerateWorkflow(
        isXs,
        false,
        items[idToEdit]?.sections[BasketSections.QUOTE]?.payload as BasketQuote,
        action
      );
      const screen = wf.screens.find((s) => s.modules.includes(action)) || wf.screens[0];
      change(idToEdit, action);
      track({ type: 'basket_action_click', payload: { action: action, id: idToEdit } });
      navigate(
        {
          pathname: `../${screen.path}`,
          search: createSearchParams({
            [BASKETITEMID]: idToEdit,
            action: action
          }).toString()
        },
        { relative: 'path' }
      );
    },
    [change, track, navigate, isXs, items]
  );

  const onDuplicate = useCallback(
    async (source: string, idToDup: string, incParcels: boolean) => {
      try {
        const action = incParcels ? 'duplicate' : 'duplicate-service';
        const newItem = await duplicate(idToDup, incParcels);
        const wf = GenerateWorkflow(
          isXs,
          false,
          newItem.sections[BasketSections.QUOTE]?.payload as BasketQuote,
          action
        );
        const screen = wf.screens.find((s) => s.modules.includes('Origin')) || wf.screens[0];
        change(newItem.id, action);
        track({
          type: 'basket_action_click',
          payload: { action: action, id: idToDup, incParcels: incParcels, source: source }
        });
        navigate(
          {
            pathname: `../${screen.path}`,
            search: createSearchParams({
              [BASKETITEMID]: newItem.id,
              action: action
            }).toString()
          },
          { relative: 'path' }
        );
      } catch (e) {
        console.error(e);
      }
    },
    [duplicate, navigate, change, isXs]
  );

  const onAdd = useCallback(() => {
    track({ type: 'basket_action_click', payload: { action: 'add-new-item' } });
    navigate({ pathname: `../quote` }, { relative: 'path' });
  }, [navigate, track]);

  const onRemove = useCallback(
    async (idToRem: string) => {
      try {
        const item = items[idToRem];
        track({
          type: 'remove_from_cart',
          payload: basketItemToRemoveItemEvent(item)
        });
      } catch (e) {
        console.error('Failed to raise remove from cart event', e);
      }
      await remove(idToRem);
    },
    [items, remove, track]
  );

  const onClearMultiDestination = useCallback(async () => {
    for (const item of stagedBasketItems) {
      await onRemove(item.name);
    }
  }, [stagedBasketItems, onRemove]);

  const reset = () => {
    setCreating(false);
    setCreationErrors(undefined);
  };

  const onSubmit = useCallback(async () => {
    setCreating(true);
    const response = await createOrder();
    if (response?.redirect) {
      try {
        publishTags({
          'order-created': 'true',
          discount: validation.breakdown?.total?.discount ?? 0,
          total: validation.breakdown?.total?.net ?? 0,
          surcharges: validation.breakdown ? GetUnderpayments(validation.breakdown.total.breakdown) : 0,
          items: basketItems.length,
          orderId: response.orderId
        });

        track({
          type: 'begin_checkout',
          payload: basketToBeginCheckoutEvent(basketItems, validation.breakdown, discountCode)
        });
      } catch (e) {
        console.error('Failed to raise checkout event', e);
      }

      if (settings[PublicSettings.ORDERPROCESS.PAYMENTSENABLED] === 'true') {
        if (settings[PublicSettings.ORDERPROCESS.BASKETPAYMENTS] === 'true') {
          setPayment(response);
        } else {
          navigate(`../payment?id=${response.orderId}&hash=${response.hash}`, { relative: 'path' });
        }
      } else {
        endReplay();

        setTimeout(() => {
          try {
            end();
          } catch (e) {
            console.error('Failed to end analytics session', e);
          }

          Sentry.close(2000)
            .then(function () {
              window.location.href = response.redirect;
            })
            .catch(function () {
              window.location.href = response.redirect;
            });
        }, 2000);
      }
    } else if (response.errors) {
      setCreationErrors(response.errors);
    }
  }, [createOrder, settings, validation.breakdown, basketItems, track, discountCode, navigate, end]);

  const workflow: Workflow = useMemo(() => {
    const quote = basketItems[0]?.item?.sections[BasketSections.QUOTE]?.payload as BasketQuote;
    const customs =
      !!quote && Object.entries(basketItems).some((item) => !!item[1].item?.sections[BasketSections.CUSTOMS]?.payload);
    if (quote) {
      return GenerateWorkflow(isXs, customs, quote, null);
    }
    return { screens: [], name: 'default' };
  }, [isXs, basketItems]);

  const fullProtectionUpgrade = useMemo(() => {
    return fullProtectionPrice(basketItems);
  }, [basketItems]);

  const onUpgradeProtection = useCallback(async () => {
    setShowProtection(false);
    await upgradeProtection(fullProtectionUpgrade.changes);
    track({ type: 'basket_protection_upgraded', payload: fullProtectionUpgrade.price });
    onSubmit();
  }, [fullProtectionUpgrade.changes, fullProtectionUpgrade.price, onSubmit, track, upgradeProtection]);

  const continueWithoutProtectionUpgrade = useCallback(() => {
    setShowProtection(false);
    onSubmit();
    track({ type: 'basket_protection_upgrade_dismissed', payload: fullProtectionUpgrade.price });
  }, [fullProtectionUpgrade.price, onSubmit, track]);

  const onBasketCompleteClick = useCallback(() => {
    if (fullProtectionUpgrade.price.net > 0) {
      setShowProtection(true);
      track({ type: 'basket_protection_upgrade_displayed', payload: fullProtectionUpgrade });
    } else {
      onSubmit();
    }
  }, [fullProtectionUpgrade, onSubmit, track]);

  return (
    <P2GContainer maxWidth="lg">
      <P2GBox pb={4}>
        {workflow && <Stepper workflow={workflow} path="confirm" disableNavigation={true} />}
        <P2GGrid container spacing={2} direction="column" wrap="nowrap" dataTestId="basket-wrapper">
          {!ready && (
            <P2GGrid item>
              <Loading />
            </P2GGrid>
          )}
          {ready && isXs && (
            <P2GGrid item>
              <P2GText variant="h2">{t(`STEPPER_STEP_CONFIRM`, { defaultValue: ToStepperTitle('confirm') })}</P2GText>
            </P2GGrid>
          )}
          {ready && stagedBasketItems.length > 0 && (
            <Outstanding
              wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
              items={stagedBasketItems}
              onEdit={(idToEdit) => onEdit(idToEdit, 'split')}
              onClear={onClearMultiDestination}
            />
          )}
          {ready && basketItems.length === 0 && (
            <P2GGrid item>
              <EmptyBasketMessage />
            </P2GGrid>
          )}
          {ready && basketItems.length > 0 && (
            <>
              {isXs && basketItems.length > 1 && (
                <P2GGrid item>
                  <AddAnother
                    type="ADD"
                    onAdd={onAdd}
                    isXs={isXs}
                    onDuplicate={(idToDup, inc) => onDuplicate('addanother', idToDup, inc)}
                  />
                </P2GGrid>
              )}
              <P2GGrid item>
                <Basket
                  id={id}
                  items={basketItems}
                  onEdit={onEdit}
                  onDuplicate={(idToDup) => onDuplicate('basket', idToDup, true)}
                  onRemove={onRemove}
                  loading={loading}
                  errors={validation?.errors}
                />
              </P2GGrid>
              <P2GGrid item>
                <P2GGrid container spacing={2}>
                  <P2GGrid item>
                    <AddAnother
                      type="ADD"
                      onAdd={onAdd}
                      isXs={isXs}
                      onDuplicate={(idToAdd, inc) => onDuplicate('addanother', idToAdd, inc)}
                    />
                  </P2GGrid>
                  <P2GGrid item>
                    <AddAnother
                      type="DUPLICATE"
                      onAdd={onAdd}
                      isXs={isXs}
                      onDuplicate={(idToDup, inc) => onDuplicate('addanother', idToDup, inc)}
                    />
                  </P2GGrid>
                </P2GGrid>
              </P2GGrid>
              {!validation.errors && (
                <>
                  <Total items={basketItems} wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}>
                    <DiscountCode
                      wrapper={(x) => (
                        <P2GBox style={{ maxWidth: '550px' }} mt={2}>
                          {x}
                        </P2GBox>
                      )}
                      addDiscountCode={addDiscountCode}
                      discountCode={discountCode}
                      warnings={validation.warnings}
                      discount={validation.breakdown?.total?.discount}
                    />
                  </Total>

                  <P2GGrid item>
                    <P2GGrid container alignItems="center" spacing={2} textAlign={'center'}>
                      {!isXs && <P2GGrid item flexGrow={1} order={{ sm: 2 }}></P2GGrid>}
                      <P2GGrid item xs={12} sm="auto" order={{ xs: 3, sm: 3 }}>
                        <Trans
                          i18nKey="BASKET_TERMS_AND_CONDITIONS_MESSAGE"
                          t={t}
                          defaults="View our <0>terms and conditions</0>"
                          components={[
                            <P2GLink
                              style={{ fontWeight: 'bold' }}
                              href="javascript:void(0)"
                              onClick={() => setShowTerms(true)}
                              dataTestId="terms-and-conditions-link"
                            />
                          ]}
                        />
                      </P2GGrid>
                      <P2GGrid item xs={12} md="auto" order={{ xs: 2, sm: 4 }} justify="flex-end">
                        <CTAButton
                          loading={loading || creating}
                          onClick={onBasketCompleteClick}
                          fullWidth
                          disabled={hasQuoteErrors || !!validation.errors || validation.validating}
                          labelTranslationKey="BASKET_TERMS_AND_CONDITIONS_BUTTON"
                          defaultLabel="Accept Terms and Continue"
                        />
                        <BasketProtectionDialog
                          open={showProtection}
                          onClose={() => setShowProtection(false)}
                          protection={fullProtectionUpgrade}
                          onContinue={continueWithoutProtectionUpgrade}
                          onUpgrade={onUpgradeProtection}
                        />
                      </P2GGrid>
                    </P2GGrid>
                    <Suspense>
                      <P2GAdaptiveOverlay
                        isOpen={showTerms}
                        onClose={() => setShowTerms(false)}
                        modalTitle=""
                        closeButtonText={t('CLOSE_DRAWER', { defaultValue: 'Close' })}
                      >
                        <P2GBox p={2}>
                          <CmsContent contentKey="terms-and-conditions" />
                        </P2GBox>
                      </P2GAdaptiveOverlay>
                    </Suspense>
                  </P2GGrid>
                </>
              )}
              {!!validation.errors && (
                <P2GGrid item>
                  <P2GAlert type="error" dataTestId="basket-errors">
                    <P2GText>{t('BASKET_ERRORS', { defaultValue: 'There are errors in your basket' })}</P2GText>
                  </P2GAlert>
                </P2GGrid>
              )}
            </>
          )}
        </P2GGrid>
        <BasketProcessing open={creating} creationErrors={creationErrors} onClose={reset} order={payment} />
      </P2GBox>
    </P2GContainer>
  );
}

Component.displayName = 'Basket Checkout Step';
